import { GroupOutlined, SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Form, Input, Row, Space, Tag, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import MainLayout from 'src/components/layouts/MainLayout';
import styles from 'src/styles/layout.module.css';
import { useDispatch } from 'react-redux';
import tileStyles from 'src/styles/tile.module.css';
import { AppDispatch } from 'src/index';
import { useForm } from 'antd/es/form/Form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDebounce } from 'react-use';
import { useTemplates, useTemplatesState } from 'src/features/templates/templates.hooks';
import { createTemplate, fetchTemplates } from 'src/features/templates/templates.state';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';

const emptyEntity = { name: '' };

const ContentTemplatesPage: React.FC = () => {
  const collections = useTemplates();
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const intl = useIntl();
  const templatesState = useTemplatesState();

  const [name, setName] = useState<string | undefined>(undefined);
  const [createMode, setCreateMode] = useState<boolean>(false);
  const [newEntity, setNewEntity] = useState<{ name: string }>(emptyEntity);
  const [form] = useForm();

  const handleFormSubmit = useCallback(async () => {
    const response = await dispatch(createTemplate(newEntity));

    if (createTemplate.fulfilled.match(response)) {
      history.push(`/content/templates/${response.payload.id}`);
    }
  }, [dispatch, history, newEntity]);
  useDebounce(
    () => {
      if (name !== undefined) {
        dispatch(fetchTemplates({ params: { name: name } }));
      }
    },
    300,
    [dispatch, name]
  );

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/content/templates'>
                <Space>
                  <GroupOutlined />
                  <FormattedMessage id={'view.content.templates.title'} />
                </Space>
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            <FormattedMessage id={'view.content.templates.title'} />
          </Typography.Title>
          <div>
            <Input
              autoFocus
              allowClear
              placeholder={intl.formatMessage({ id: 'view.content.templates.input.search.placeholder' })}
              prefix={<SearchOutlined />}
              onChange={e => {
                setName(e.target.value);
              }}
              style={{ marginBottom: 10 }}
            />
            <LoaderWrapper loading={templatesState.isPending}>
              <div className={styles.content}>
                {createMode && (
                  <Form
                    form={form}
                    validateTrigger='onBlur'
                    layout='vertical'
                    className={tileStyles.listTile}
                    onFinish={handleFormSubmit}
                  >
                    <Form.Item name='name' noStyle>
                      <Input
                        autoFocus
                        type='text'
                        name='name'
                        placeholder={intl.formatMessage({ id: 'view.content.templates.form.title.placeholder' })}
                        onChange={ev => setNewEntity(e => ({ ...e, name: ev.target.value }))}
                      />
                    </Form.Item>
                    <Button
                      style={{ marginLeft: 10 }}
                      htmlType='submit'
                      type='primary'
                      disabled={newEntity.name === ''}
                    >
                      <FormattedMessage id={'action.create'} />
                    </Button>
                    <Button
                      style={{ marginLeft: 10 }}
                      htmlType='button'
                      type='default'
                      onClick={() => setCreateMode(false)}
                    >
                      <FormattedMessage id={'action.cancel'} />
                    </Button>
                  </Form>
                )}
                {collections.length === 0 && (
                  <Row justify='center' style={{ width: '100%', margin: '16px 0' }}>
                    <Typography.Text>
                      <FormattedMessage id={'text.NothingFound'} />
                    </Typography.Text>
                  </Row>
                )}
                {collections.map(c => (
                  <div key={c.id} className={tileStyles.listTile}>
                    <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                      <Space>
                        <Link to={`/content/templates/${c.id}`} className={tileStyles.tileName}>
                          <GroupOutlined />
                          &nbsp;
                          {c.name}
                        </Link>
                      </Space>
                      <Space>
                        <Tag>{c.publishedAt ? 'Опубликован' : 'Не опубликован'}</Tag>
                      </Space>
                    </Space>
                  </div>
                ))}
              </div>
            </LoaderWrapper>
          </div>
        </div>
        <div className={styles.sidebar}>
          <Space>
            <Button type='primary' onClick={() => setCreateMode(true)}>
              <FormattedMessage id={'view.content.templates.action.create'} />
            </Button>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const ContentTemplatesPageBlock: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchTemplates({}));
  }, [dispatch]);

  return <ContentTemplatesPage />;
};

export default ContentTemplatesPageBlock;
